// ── Inspection Status System ───────────────────────────────────────
// Status ordering + the delivery-date helper. The effective-status RULE now
// lives on the backend (backend/inspection_status.py) — it is computed there
// and returned as `effectiveStatus` on every inspection, so the frontend only
// renders it. `computeDeliveryDate` stays here for the live "Due date" preview
// in AssessmentSettingsModal (recomputed as the user edits the reporting
// period before saving) and the ObjectDetail due-date label.

/** Ordered list of all valid statuses — preserves display order in dropdowns. */
export const STATUS_ORDER = [
  'Planning',
  'Execution',
  'Handover',
  'Reporting',
  'Delivery',
  'Delayed',
  'Missing data',
];

/**
 * Computes the delivery (deadline) date from an assessment start date and a
 * reporting period string from the ddl_reportingPeriod DDL collection.
 *
 * Supported period formats (case-insensitive):
 *   "N Week(s)"  → N × 7 calendar days added to startDate
 *   "N Day(s)"   → N calendar days added to startDate
 *   "No Deadline" (or any string matching /no\s*deadline/i) → returns null
 *
 * @param {string|null} startDateStr    — "YYYY-MM-DD" assessment start date
 * @param {string|null} reportingPeriod — e.g. "2 Weeks", "14 Days", "No Deadline"
 * @returns {string|null} "YYYY-MM-DD" delivery date, or null if no deadline / not computable
 */
export function computeDeliveryDate(startDateStr, reportingPeriod) {
  if (!startDateStr || !reportingPeriod) return null;
  if (/no\s*deadline/i.test(reportingPeriod)) return null;

  const weekMatch = reportingPeriod.match(/^(\d+)\s+[Ww]eek/);
  const dayMatch  = reportingPeriod.match(/^(\d+)\s+[Dd]ay/);

  let days = 0;
  if (weekMatch) {
    days = parseInt(weekMatch[1], 10) * 7;
  } else if (dayMatch) {
    days = parseInt(dayMatch[1], 10);
  } else {
    return null; // unrecognised format
  }

  const start = new Date(startDateStr + 'T00:00:00');
  start.setDate(start.getDate() + days);
  // Return as "YYYY-MM-DD" from LOCAL date parts. (toISOString() converts
  // local midnight to UTC, which east of UTC — NL, LK — is the previous day,
  // so the shown deadline was a day earlier than the backend's.)
  const pad = (n) => String(n).padStart(2, '0');
  return `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}`;
}
